<script setup>
import {onMounted, ref} from 'vue';
import Button from '../../components/forms/Button.vue';
import ClientService from '@/services/ClientService';
import {createConfirmDialog} from 'vuejs-confirm-dialog';
import {formatDate, showSuccessMessage} from '@/helper';
import router from '@/router';
import {useDataTable} from '@/utils/datatableHelper';
import ModalDialog from '@/components/modals/ModalDialog.vue';
import {useI18n} from 'vue-i18n';

const {t} = useI18n();

const datatableRef = ref(null);
const datatable = useDataTable(fetchArchivedClients, 'fullname', 10, true, {}, 'CLIENTS-ARCHIVE', datatableRef);

const columns = ref([
  {
    label: t('clientsArchive.name'),
    field: 'firstname',
    filterOptions: {enabled: true, skipDiacritics: true, placeholder: ' '},
  },
  {
    label: t('clientsArchive.surname'),
    field: 'surname',
    filterOptions: {enabled: true, skipDiacritics: true, placeholder: ' '},
  },
  {
    label: t('clientsArchive.email'),
    field: 'email',
    filterOptions: {enabled: true, skipDiacritics: true, placeholder: ' '},
  },
  {
    label: t('clientsArchive.intermediary_info'),
    field: 'intermediary_info',
    sortable: false,
    filterOptions: {enabled: true, skipDiacritics: true, placeholder: ' '},
  },
  {
    label: t('clientsArchive.archived_at'),
    field: 'deleted_at',
    sortable: false,
    filterOptions: {enabled: false},
  },
  {
    label: '',
    field: 'action',
    sortable: false,
    html: true,
  },
]);

onMounted(() => {
  // fetchArchivedClients is triggered automatically by useDataTable on mount
});

function fetchArchivedClients(body) {
  ClientService.fetchArchivedRecords(body).then(response => {
    datatable.totalRecords.value = response.meta.total;
    datatable.rows.value = response.data;
    datatable.isLoading.value = false;
  });
}

/** Otevře confirm dialog pro obnovu klienta a po potvrzení provede restore. */
function restoreClient(clientId) {
  const {reveal, onConfirm} = createConfirmDialog(ModalDialog, {
    title: t('clientsArchive.restore_confirm_title'),
    message: t('clientsArchive.restore_confirm_message'),
  });
  onConfirm(() => {
    ClientService.restoreClient(clientId).then(response => {
      showSuccessMessage(response.message);
      fetchArchivedClients(datatable.body);
    });
  });
  reveal();
}
</script>

<template>
  <div class="page-section">
    <div class="page-section__heading">
      <div class="row">
        <div class="col-3">
          <a @click="router.back()" class="page-section__heading__back">
            <i class="mdi mdi-arrow-left"></i> {{ t('clientsArchive.back') }}
          </a>
        </div>
        <div class="col-5 text-center">
          <div class="page-section__title">{{ t('clientsArchive.title') }}</div>
        </div>
      </div>
    </div>
  </div>

  <div class="page-section__content">
    <div class="mt-3">
      <vue-good-table
          ref="datatableRef"
          :columns="columns"
          :rows="datatable.rows.value"
          mode="remote"
          @page-change="datatable.onPageChange"
          @sort-change="datatable.onSortChange"
          @column-filter="datatable.onColumnFilter"
          @per-page-change="datatable.onPerPageChange"
          :totalRows="datatable.totalRecords.value"
          :pagination-options="{
            enabled: true,
            rowsPerPageLabel: t('clientsArchive.entriesPerPage'),
            nextLabel: t('clientsArchive.next'),
            prevLabel: t('clientsArchive.previous'),
            ofLabel: 'z',
          }">
        <template #table-row="props">
          <span v-if="props.column.field === 'action'" style="float: right">
            <!-- Odkaz na detail archivovaného klienta -->
<!--            <router-link :to="{name: 'ClientDetail', params: {id: props.row.id}}">
              <button type="button" class="btn btn-link btn-sm">
                <i class="mdi mdi-eye"/>
              </button>
            </router-link>-->
            <!-- Tlačítko pro obnovu klienta s confirm dialogem -->
            <button type="button" class="btn btn-link btn-sm" @click="restoreClient(props.row.id)" :title="t('clientsArchive.restore')">
              <i class="mdi mdi-restore"/>
            </button>
          </span>
          <span v-if="props.column.field === 'intermediary_info'">
            <span>{{ props.row.intermediary_person?.label }}</span><br/>
            <small>{{ props.row.intermediary_person?.intermediary?.label }}</small>
          </span>
          <span v-if="props.column.field === 'deleted_at'">
            {{ props.row.deleted_at ? formatDate(props.row.deleted_at, 'd. m. Y') : '---' }}
          </span>
        </template>
        <template v-slot:emptystate v-if="!datatable.isLoading.value">
          <div class="text-center">{{ t('clientsArchive.no_records_found') }}</div>
        </template>
        <template v-slot:emptystate v-if="datatable.isLoading.value">
          <div class="text-center">{{ t('clientsArchive.loading') }}</div>
        </template>
      </vue-good-table>
    </div>
  </div>
</template>